import * as React from 'react';
import { useHttpClient } from 'hooks';
import { Entity, MappedEntityType } from 'services/resolvers';
import { OwnUserWithCompany, Notification } from 'services/resolvers/Common';
import { AppContext, AppContextType } from 'context/AppContext';
import { PropertyAggregation } from './properties/PropertyDefinition';
import { FullUser } from 'services/resolvers/Users';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'berry/store/slices/snackbar';

export const useMyself = (setOnRender: boolean = true) => {
    const { asyncDetachedRequest: getMyself } = useHttpClient<OwnUserWithCompany>(Entity.Generic, MappedEntityType.GetMyself);
    const { setAppToken, setOwnUser } = React.useContext(AppContext) as AppContextType;

    const fetchMyself = async () => {
        const serverMyself = await getMyself({});
        if (serverMyself) {
            setOwnUser(serverMyself);
        } else {
            setAppToken(undefined);
        }
    };

    React.useEffect(() => {
        if (setOnRender) {
            fetchMyself();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { fetchMyself };
};

export type PropertiesManagerType = {
    loaded: boolean;
    properties: Record<string, string | number | boolean>;
    isPropertyEnabled: (property: PropertyAggregation, defaultValue?: boolean) => boolean;
    numberPropertyValue: (property: PropertyAggregation, defaultValue: number) => number;
    setProperties: (properties: Record<string, string | number | boolean>) => void;
    resetProperties: () => void;
};

export const useProperties = (): PropertiesManagerType => {
    const [loaded, setLoaded] = React.useState(false);
    const [properties, setProperties] = React.useState<Record<string, string | boolean | number>>({});

    // eslint-disable-next-line arrow-body-style
    const isPropertyEnabled = (property: PropertyAggregation, defaultValue: boolean = false) => {
        if (property in properties) {
            return properties[property] as boolean;
        }
        return defaultValue;
    };

    // eslint-disable-next-line arrow-body-style
    const numberPropertyValue = (property: PropertyAggregation, defaultValue: number = 0) => {
        return defaultValue;
    };

    const setPropertiesWrapper = (newProperties: Record<string, string | number | boolean>) => {
        setProperties(newProperties);
        setLoaded(true);
    };

    const resetProperties = () => {
        setProperties({});
        setLoaded(false);
    };

    return { loaded, properties, isPropertyEnabled, numberPropertyValue, setProperties: setPropertiesWrapper, resetProperties };
};

export const useContextProperties = (): PropertiesManagerType & { ownUser: OwnUserWithCompany | null } => {
    const { propertyManager, ownUser } = React.useContext(AppContext) as AppContextType;

    // TODO ADD PAAS CHECK METHODS
    return { ...propertyManager, ownUser };
};

export const useNotificationList = () => {
    const { asyncDetachedRequest: getNotifications } = useHttpClient<Notification[]>(Entity.Generic, MappedEntityType.NotificationList);
    const [notificationList, setNotificationList] = React.useState<Notification[]>([]);

    const fetchNotificationList = async () => {
        const serverNotificationList = await getNotifications({});
        if (serverNotificationList) {
            setNotificationList(serverNotificationList);
        }
    };

    React.useEffect(() => {
        fetchNotificationList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addNotification = (notification: Notification) => {
        setNotificationList((prevNotificationList) => [notification].concat(prevNotificationList));
    };

    return { notificationList, addNotification };
};

export const useFullMyself = () => {
    const { asyncDetachedRequest: getMyself } = useHttpClient<FullUser>(Entity.Users, MappedEntityType.GetFullMyself);
    const [fullMyself, setFullMyself] = React.useState<FullUser | null>(null);

    const fetchMyself = async () => {
        const serverMyself = await getMyself({});
        if (serverMyself) {
            setFullMyself(serverMyself);
        }
    };

    React.useEffect(() => {
        fetchMyself();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { fullMyself, fetchMyself };
};

export const useSnackbar = () => {
    const dispatch = useDispatch();

    const showSnackbar = (message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'success') => {
        dispatch(
            openSnackbar({
                open: true,
                message,
                variant: 'alert',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
                alert: {
                    color: variant
                }
            })
        );
    };

    return { showSnackbar };
};
